import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LOGO } from "../lib/site";
import { StripedTriangle, OrangeArc } from "../components/Motifs";

const AdminLogin = () => {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && typeof user === "object") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="admin-login-page">
      <div className="grow grid lg:grid-cols-2">
        <div className="relative hidden lg:block bg-ink overflow-hidden">
          <StripedTriangle className="absolute top-0 left-0 opacity-30" size={360} />
          <OrangeArc className="absolute bottom-0 right-0" size={220} />
          <div className="relative h-full flex flex-col justify-between p-12 text-cream">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO} alt="Elenem" className="h-11 w-11 bg-cream p-1" />
              <span className="font-display text-xl">Elenem <em className="text-orange-brand not-italic">Strategies</em></span>
            </Link>
            <p className="font-display italic text-4xl leading-tight max-w-md">
              Curiosity is a strategy. Signing in.
            </p>
            <span className="text-cream/50 text-xs">Admin access</span>
          </div>
        </div>

        <div className="flex items-center justify-center px-6 py-16">
          <form onSubmit={submit} className="w-full max-w-md space-y-8" data-testid="admin-login-form">
            <div>
              <div className="overline">Admin</div>
              <h1 className="mt-2 font-display text-4xl text-ink">Sign in</h1>
              <p className="mt-2 text-ink-soft text-sm">
                Access contact form and newsletter submissions.
              </p>
            </div>
            <div className="space-y-6">
              <div>
                <label className="overline block mb-2" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email"
                  className="w-full bg-transparent border-b border-ink/30 focus:border-cobalt outline-none py-3 text-ink"
                />
              </div>
              <div>
                <label className="overline block mb-2" htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password"
                  className="w-full bg-transparent border-b border-ink/30 focus:border-cobalt outline-none py-3 text-ink"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-cobalt text-cream px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-cobalt-deep transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <Link to="/" className="block text-center text-sm text-ink-soft hover:text-cobalt link-underline">
              ← Back to site
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
